import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";

export type HatKind = "none" | "cap" | "pointy" | "crown" | "helm" | "hood";

/** Everything the villager can be customised with. */
export interface CharacterSpec {
  skin?: number;
  hair?: number;
  /** Shirt colour; the closest of the game's own designs is used. */
  tunic?: number;
  trim?: number;
  pants?: number;
  boots?: number;
  cape?: number;
  hat?: HatKind;
  hatColor?: number;
  /** Uniform scale; 1 is one villager tall. */
  scale?: number;
  sword?: boolean;
  shield?: boolean;
  staff?: boolean;
}

interface Manifest {
  model: string;
  height: number;
  clips: string[];
  shirts: { file: string; color: number }[];
  tint: Record<string, string[]>;
}

const ASSET_DIR = "/models/villager/";

/** Blend time between locomotion states, in seconds. */
const FADE = 0.18;

let asset: {
  scene: THREE.Group;
  clips: THREE.AnimationClip[];
  manifest: Manifest;
  blink: THREE.Texture;
} | null = null;

/**
 * Loads the villager once, for everybody. Call and await this before the
 * first scene starts; `new CharacterModel()` is synchronous and needs it.
 */
export async function loadCharacterAssets(): Promise<void> {
  if (asset) return;
  const loader = new GLTFLoader();
  const [gltf, manifest] = await Promise.all([
    loader.loadAsync(ASSET_DIR + "villager.gltf"),
    fetch(ASSET_DIR + "villager.json").then((r) => r.json() as Promise<Manifest>),
  ]);
  gltf.scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = true;
    mesh.receiveShadow = false;
    // The model is 4-bit colour-indexed art at 8 to 32 pixels a tile. Anything
    // but nearest turns the eyes into smudges.
    for (const m of materials(mesh)) {
      const map = (m as THREE.MeshStandardMaterial).map;
      if (map) {
        map.magFilter = THREE.NearestFilter;
        map.minFilter = THREE.NearestFilter;
        map.generateMipmaps = false;
        map.needsUpdate = true;
      }
    }
  });
  // Textures have to be resident before the first character is built: one
  // still in flight renders black, not white.
  const [, blink] = await Promise.all([
    loadShirts(manifest),
    new THREE.TextureLoader().loadAsync(ASSET_DIR + "blink.png"),
  ]);
  blink.magFilter = blink.minFilter = THREE.NearestFilter;
  blink.generateMipmaps = false;
  blink.colorSpace = THREE.SRGBColorSpace;
  asset = { scene: gltf.scene as THREE.Group, clips: gltf.animations, manifest, blink };
}

function materials(mesh: THREE.Mesh): THREE.Material[] {
  return Array.isArray(mesh.material) ? mesh.material : [mesh.material];
}

/** The game's other shirt designs, shared by every villager wearing one. */
const shirtTextures: THREE.Texture[] = [];

async function loadShirts(manifest: Manifest): Promise<void> {
  const loader = new THREE.TextureLoader();
  const loaded = await Promise.all(
    manifest.shirts.map((s) => loader.loadAsync(ASSET_DIR + s.file)),
  );
  for (const tex of loaded) {
    tex.magFilter = THREE.NearestFilter;
    tex.minFilter = THREE.NearestFilter;
    tex.generateMipmaps = false;
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.colorSpace = THREE.SRGBColorSpace;
    shirtTextures.push(tex);
  }
}

/** Which of the game's own shirt designs sits closest to a requested colour. */
function nearestShirt(color: number): number {
  const shirts = asset!.manifest.shirts;
  let best = 0;
  let bestDistance = Infinity;
  for (let i = 0; i < shirts.length; i++) {
    const d = colorDistance(shirts[i].color, color);
    if (d < bestDistance) {
      bestDistance = d;
      best = i;
    }
  }
  return best;
}

function colorDistance(a: number, b: number): number {
  // Weighted toward green the way perception is; close enough to pick a shirt.
  const dr = ((a >> 16) & 255) - ((b >> 16) & 255);
  const dg = ((a >> 8) & 255) - ((b >> 8) & 255);
  const db = (a & 255) - (b & 255);
  return 2 * dr * dr + 4 * dg * dg + 3 * db * db;
}

/**
 * A villager. The mesh, rig and animation are the Animal Forest e+ player
 * model, ripped by `af/tools/villager.py`; everything here is instancing and
 * playback. Authored facing +Z, one unit tall.
 */
export class CharacterModel {
  readonly object3D = new THREE.Group();

  /** Height of the top of the head, in local units before `scale`. */
  readonly height = 1;

  private readonly mixer: THREE.AnimationMixer;
  private readonly actions = new Map<string, THREE.AnimationAction>();
  private readonly owned: (THREE.Material | THREE.Texture)[] = [];
  private readonly head: THREE.Object3D | null;

  private locomotion: THREE.AnimationAction | null = null;
  private override: THREE.AnimationAction | null = null;
  private walking = false;

  /** The eye strip has an open and a shut frame; blinking swaps between them. */
  private eyes: THREE.MeshStandardMaterial | null = null;
  private eyesOpen: THREE.Texture | null = null;
  private blinkTimer = 1 + Math.random() * 4;
  private blinking = false;

  constructor(spec: CharacterSpec = {}) {
    if (!asset) {
      throw new Error("await loadCharacterAssets() before building characters");
    }

    const root = cloneSkinned(asset.scene);
    this.object3D.add(root);
    this.object3D.scale.setScalar(spec.scale ?? 1);
    this.head = root.getObjectByName("head") ?? null;

    this.recolour(root, spec);
    this.eyes = this.ownEyeMaterial(root);
    this.eyesOpen = this.eyes?.map ?? null;

    this.mixer = new THREE.AnimationMixer(root);
    for (const clip of asset.clips) {
      const action = this.mixer.clipAction(clip);
      action.enabled = true;
      this.actions.set(clip.name, action);
    }
    this.locomotion = this.actions.get("idle") ?? null;
    this.locomotion?.play();

    const hat = buildHat(spec.hat ?? "none", spec.hatColor ?? spec.trim ?? 0xffd23f, spec.trim ?? 0xffd23f);
    if (hat && this.head) {
      this.head.add(hat);
      hat.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (mesh.isMesh) {
          this.owned.push(mesh.material as THREE.Material);
          mesh.castShadow = true;
        }
      });
    }
  }

  /**
   * Clones only the materials a spec actually changes. Textures that carry
   * real detail — the face, the shirt design — are swapped rather than
   * tinted, because multiplying a colour through them muddies both.
   */
  private recolour(root: THREE.Object3D, spec: CharacterSpec): void {
    const tint = asset!.manifest.tint;
    const wanted = new Map<string, number>();
    const add = (key: keyof CharacterSpec, value: number | undefined) => {
      if (value === undefined) return;
      for (const name of tint[key] ?? []) wanted.set(name, value);
    };
    add("hair", spec.hair);
    add("boots", spec.boots);
    add("pants", spec.pants);
    add("skin", spec.skin);

    const shirt = spec.tunic === undefined ? null : shirtTextures[nearestShirt(spec.tunic)];

    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const list = materials(mesh);
      let changed = false;
      const next = list.map((m) => {
        const colour = wanted.get(m.name);
        const swap = shirt && m.name === "shirt";
        if (colour === undefined && !swap) return m;
        changed = true;
        const copy = m.clone() as THREE.MeshStandardMaterial;
        if (colour !== undefined) copy.color.setHex(colour);
        if (swap) copy.map = shirt;
        this.owned.push(copy);
        return copy;
      });
      if (changed) mesh.material = Array.isArray(mesh.material) ? next : next[0];
    });
  }

  /** Clones the eye material so this villager can blink on its own clock. */
  private ownEyeMaterial(root: THREE.Object3D): THREE.MeshStandardMaterial | null {
    let found: THREE.MeshStandardMaterial | null = null;
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (found || !mesh.isMesh) return;
      const m = mesh.material as THREE.MeshStandardMaterial;
      if (Array.isArray(mesh.material) || m.name !== "eyes") return;
      found = m.clone() as THREE.MeshStandardMaterial;
      mesh.material = found;
      this.owned.push(found);
    });
    return found;
  }

  setWalking(walking: boolean): void {
    if (walking === this.walking) return;
    this.walking = walking;
    this.play(walking ? "walk" : "idle");
  }

  /** Point the whole model at a Y angle, in radians. */
  setFacing(angle: number): void {
    this.object3D.rotation.y = angle;
  }

  update(dt: number): void {
    this.mixer.update(dt);

    this.blinkTimer -= dt;
    if (this.blinkTimer <= 0 && this.eyes && this.eyesOpen) {
      this.blinking = !this.blinking;
      this.blinkTimer = this.blinking ? 0.1 : 2 + Math.random() * 4;
      this.eyes.map = this.blinking ? asset!.blink : this.eyesOpen;
    }
  }

  /**
   * Poses the attack animation by hand: the battle scene drives its own
   * timing, so it wants a position in the swing rather than a playback.
   */
  setSwing(amount: number): void {
    const action = this.actions.get("swing");
    if (!action) return;
    if (amount <= 0) {
      if (this.override) {
        this.override.stop();
        this.override = null;
        this.locomotion?.setEffectiveWeight(1);
      }
      return;
    }
    if (this.override !== action) {
      this.override?.stop();
      this.override = action;
      action.reset().play();
      action.paused = true;
      this.locomotion?.setEffectiveWeight(0);
    }
    const clip = action.getClip();
    action.time = Math.min(1, amount) * clip.duration;
    this.mixer.update(0);
  }

  /** Plays one of the ripped clips by name; falls back to the idle. */
  play(name: string): void {
    const next = this.actions.get(name) ?? this.actions.get("idle");
    if (!next || next === this.locomotion) return;
    next.reset().setEffectiveWeight(1).play();
    if (this.locomotion) this.locomotion.crossFadeTo(next, FADE, false);
    this.locomotion = next;
  }

  /**
   * Only the materials this instance cloned are its own. Geometry and the
   * shared textures belong to the loaded asset -- SkeletonUtils.clone reuses
   * the buffers -- so disposing them here would blank every other villager.
   */
  dispose(): void {
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.object3D);
    for (const owned of this.owned) owned.dispose();
    this.owned.length = 0;
  }
}

/**
 * The demo's RPG hats, which a villager has no equivalent for. Sizes are in
 * the model's own units and measured off the ripped skull: the head bone sits
 * at the neck, not at the head's centre, so everything here is placed from
 * the crown down rather than from the bone out.
 */
const CROWN = 2634;  // top of the skull, above the head bone
const SKULL = 1313;  // skull radius

function hatMaterial(color: number): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color });
}

function buildHat(kind: HatKind, color: number, trim: number): THREE.Object3D | null {
  if (kind === "none") return null;
  const g = new THREE.Group();
  // Inside the head bone, up is +X and forward is +Z. Turn the group so hats
  // can be built the ordinary way, with Y up.
  g.rotation.z = -Math.PI / 2;

  const r = SKULL;
  const centre = CROWN - r * 0.95;

  switch (kind) {
    case "cap": {
      const crown = new THREE.Mesh(
        new THREE.SphereGeometry(r * 1.03, 14, 7, 0, Math.PI * 2, 0, 1.25),
        hatMaterial(color),
      );
      crown.position.y = centre;
      const peak = new THREE.Mesh(
        new THREE.SphereGeometry(r, 14, 4, Math.PI * 0.24, Math.PI * 0.52, 1.15, 0.3),
        hatMaterial(color),
      );
      peak.scale.set(1.05, 0.22, 1.35);
      peak.position.y = centre + r * 0.2;
      g.add(crown, peak);
      break;
    }
    case "pointy": {
      const cone = new THREE.Mesh(new THREE.ConeGeometry(r * 0.66, r * 1.6, 8), hatMaterial(color));
      cone.position.y = centre + r * 1.05;
      const brim = new THREE.Mesh(
        new THREE.SphereGeometry(r * 1.15, 14, 3, 0, Math.PI * 2, 1.15, 0.45),
        hatMaterial(color),
      );
      brim.scale.y = 0.45;
      brim.position.y = centre + r * 0.36;
      g.add(cone, brim);
      break;
    }
    case "crown": {
      const band = new THREE.Mesh(
        new THREE.CylinderGeometry(r * 0.8, r * 0.86, r * 0.3, 14),
        hatMaterial(color),
      );
      band.position.y = centre + r * 0.62;
      g.add(band);
      for (let i = 0; i < 5; i++) {
        const spike = new THREE.Mesh(
          new THREE.ConeGeometry(r * 0.13, r * 0.36, 4),
          hatMaterial(color),
        );
        const a = (i / 5) * Math.PI * 2;
        spike.position.set(Math.cos(a) * r * 0.78, centre + r * 0.92, Math.sin(a) * r * 0.78);
        g.add(spike);
      }
      break;
    }
    case "helm": {
      const dome = new THREE.Mesh(
        new THREE.SphereGeometry(r * 1.06, 14, 8, 0, Math.PI * 2, 0, 1.45),
        hatMaterial(color),
      );
      dome.position.y = centre;
      const crest = new THREE.Mesh(
        new THREE.BoxGeometry(r * 0.12, r * 0.3, r * 1.35),
        hatMaterial(trim),
      );
      crest.position.y = centre + r * 0.86;
      g.add(dome, crest);
      break;
    }
    case "hood": {
      const hood = new THREE.Mesh(
        new THREE.SphereGeometry(r * 1.14, 16, 10, 0, Math.PI * 2, 0, 2.1),
        new THREE.MeshLambertMaterial({ color, side: THREE.DoubleSide }),
      );
      // Cut the front away so the face shows through a real opening.
      const pos = hood.geometry.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        const radial = Math.hypot(pos.getX(i), pos.getZ(i)) || 1e-6;
        const front = Math.max(0, pos.getZ(i) / radial);
        const cut = r * (-0.5 + 0.98 * front ** 1.4);
        if (pos.getY(i) < cut) pos.setY(i, cut);
      }
      pos.needsUpdate = true;
      hood.geometry.computeVertexNormals();
      hood.position.y = centre - r * 0.05;
      g.add(hood);
      break;
    }
  }
  return g;
}
